import { useState, useEffect, useCallback } from "react";
import { dilapidationService } from "@/api/services/dilapidation.service";
export function useDilapidation(jobId) {
    const [state, setState] = useState({
        dilapidation: null,
        loading: true,
        error: null,
        notFound: false,
    });
    const fetchDilapidation = useCallback(async () => {
        if (!jobId) {
            setState({
                dilapidation: null,
                loading: false,
                error: "No job ID provided",
                notFound: false,
            });
            return;
        }
        setState((prev) => ({ ...prev, loading: true, error: null, notFound: false }));
        try {
            const response = await dilapidationService.getByJobId(jobId);
            const dilapidation = response.data?.dilapidation ?? null;
            setState({
                dilapidation,
                loading: false,
                error: null,
                notFound: !dilapidation,
            });
        }
        catch (err) {
            const axiosErr = err;
            if (axiosErr?.response?.status === 404) {
                setState({
                    dilapidation: null,
                    loading: false,
                    error: null,
                    notFound: true,
                });
            }
            else {
                const errorMessage = err instanceof Error ? err.message : "Failed to load dilapidation report";
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    error: errorMessage,
                    notFound: false,
                }));
            }
        }
    }, [jobId]);
    const createDilapidation = useCallback(async () => {
        if (!jobId)
            return null;
        try {
            const response = await dilapidationService.create(jobId);
            const dilapidation = response.data?.dilapidation ?? null;
            setState((prev) => ({
                ...prev,
                dilapidation,
                notFound: false,
                error: null,
            }));
            return dilapidation;
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to create dilapidation report";
            setState((prev) => ({ ...prev, error: errorMessage }));
            return null;
        }
    }, [jobId]);
    const updateDilapidation = useCallback(async (data) => {
        if (!jobId)
            return null;
        try {
            const response = await dilapidationService.update(jobId, data);
            const dilapidation = response.data?.dilapidation ?? null;
            setState((prev) => ({ ...prev, dilapidation, error: null }));
            return dilapidation;
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to update dilapidation report";
            setState((prev) => ({ ...prev, error: errorMessage }));
            return null;
        }
    }, [jobId]);
    const updateStatus = useCallback(async (status) => {
        if (!jobId)
            return null;
        try {
            const response = await dilapidationService.updateStatus(jobId, status);
            const dilapidation = response.data?.dilapidation ?? null;
            setState((prev) => ({ ...prev, dilapidation, error: null }));
            return dilapidation;
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to update status";
            setState((prev) => ({ ...prev, error: errorMessage }));
            return null;
        }
    }, [jobId]);
    const uploadSectionDocument = useCallback(async (sectionId, file, type) => {
        try {
            const response = await dilapidationService.uploadSectionDocument(sectionId, file, type);
            const doc = response.data;
            if (doc && state.dilapidation?.sections) {
                setState((prev) => {
                    if (!prev.dilapidation?.sections)
                        return prev;
                    const sections = prev.dilapidation.sections.map((s) => s.id === sectionId
                        ? {
                            ...s,
                            documents: [...(s.documents ?? []), doc],
                        }
                        : s);
                    return {
                        ...prev,
                        dilapidation: {
                            ...prev.dilapidation,
                            sections,
                        },
                    };
                });
            }
            return doc;
        }
        catch (err) {
            return null;
        }
    }, [state.dilapidation?.sections]);
    const deleteDocument = useCallback(async (documentId) => {
        try {
            await dilapidationService.deleteDocument(documentId);
            if (state.dilapidation?.sections) {
                setState((prev) => {
                    if (!prev.dilapidation?.sections)
                        return prev;
                    const sections = prev.dilapidation.sections.map((s) => ({
                        ...s,
                        documents: (s.documents ?? []).filter((d) => d.id !== documentId),
                    }));
                    return {
                        ...prev,
                        dilapidation: {
                            ...prev.dilapidation,
                            sections,
                        },
                    };
                });
            }
            return true;
        }
        catch {
            return false;
        }
    }, [state.dilapidation?.sections]);
    const deleteDilapidation = useCallback(async () => {
        if (!jobId)
            return false;
        try {
            await dilapidationService.delete(jobId);
            setState({
                dilapidation: null,
                loading: false,
                error: null,
                notFound: true,
            });
            return true;
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to delete dilapidation report";
            setState((prev) => ({ ...prev, error: errorMessage }));
            return false;
        }
    }, [jobId]);
    useEffect(() => {
        fetchDilapidation();
    }, [fetchDilapidation]);
    return {
        ...state,
        refetch: fetchDilapidation,
        createDilapidation,
        updateDilapidation,
        updateStatus,
        uploadSectionDocument,
        deleteDocument,
        deleteDilapidation,
    };
}
